import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { DashboardStyleService } from '../services/dashboardStyle.service';
import { DashboardStyle } from '../interfaces/dashboardStyle.interface';
import { DashboardHomeGroup, useDashboardHomeInfo } from '../utils/dashboardHome';

/**
 * Who can switch the home dashboard between Tiles and Thread: a person whose home (see utils/dashboardHome,
 * the same rule Dashboard.tsx routes on) is in THREAD_ENABLED_GROUPS. Today:
 *  - board: the whole-business DashboardBoard. Default Tiles.
 *  - merch: merchandisers, who fall through to UserDashboardView and can read buyer orders. Default Thread.
 *  - sales: sales heads and managers (SalesTeamDashboard). Default Tiles.
 * The personal home is eligible only with MENU_DASHBOARD (the key its Thread lens requires on the server).
 */
export type DashboardStyleGroup = DashboardHomeGroup;

export interface DashboardStyleState {
    group: DashboardStyleGroup | null;
    /** The style to draw now (saved choice, else the group's default). */
    style: DashboardStyle;
    /** False while an eligible person's saved choice is still loading: draw nothing rather than the wrong board. */
    ready: boolean;
    /** Switch now, save on the server, and revert with a toast when the save fails. */
    setStyle: (next: DashboardStyle) => void;
}

const NOT_ELIGIBLE: DashboardStyleState = { group: null, style: 'TILES', ready: true, setStyle: () => undefined };

const DashboardStyleContext = createContext<DashboardStyleState>(NOT_ELIGIBLE);

/** A merchandiser's Tiles home: the Merchandising Dashboard page they already know. */
export const MERCH_TILES_PATH = '/merchandising/dashboard';

/** An HR manager's Tiles home: the People dashboard page they already know. */
export const PEOPLE_TILES_PATH = '/dashboard/people';

export const defaultStyleFor = (group: DashboardStyleGroup): DashboardStyle => (group === 'merch' ? 'THREAD' : 'TILES');

/** The switch group of the signed-in person: their home when it has a Thread lens, else null. */
export function useDashboardStyleGroup(): DashboardStyleGroup | null {
    const { user } = useAuth();
    const { home, threadEligible } = useDashboardHomeInfo();
    return user && threadEligible && home !== 'systemAdmin' ? home : null;
}

export const DashboardStyleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { selectedInstitutionId, permissionsReady } = useAuth();
    const toast = useToast();
    const group = useDashboardStyleGroup();

    // `saved` is what the server holds; undefined = not loaded yet, null = never chosen.
    const [saved, setSaved] = useState<DashboardStyle | null | undefined>(undefined);
    const loadedFor = useRef<string | null>(null);
    // Set when the person picks a style: a GET still in flight must then never overwrite their pick.
    const userChose = useRef(false);
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const eligible = group !== null && permissionsReady && !!selectedInstitutionId;

    useEffect(() => {
        if (!eligible || !selectedInstitutionId) { setSaved(undefined); loadedFor.current = null; return; }
        let live = true;
        setSaved(undefined);
        userChose.current = false;
        DashboardStyleService.get(selectedInstitutionId)
            .then(r => { if (live) { loadedFor.current = selectedInstitutionId; if (!userChose.current) setSaved(r.style ?? null); } })
            // a failed read must not strand the page on a spinner: draw the group's default
            .catch(() => { if (live) { loadedFor.current = selectedInstitutionId; if (!userChose.current) setSaved(null); } });
        return () => { live = false; };
    }, [eligible, selectedInstitutionId]);

    const setStyle = useCallback((next: DashboardStyle) => {
        if (!selectedInstitutionId || group === null) return;
        const institutionId = selectedInstitutionId;
        let previous: DashboardStyle | null | undefined;
        userChose.current = true;
        setSaved(prev => { previous = prev; return next; });
        DashboardStyleService.save(institutionId, next).catch(err => {
            if (loadedFor.current === institutionId) setSaved(previous === undefined ? null : previous);
            toastRef.current.showApiError?.(err, 'Could not save your dashboard style');
        });
    }, [selectedInstitutionId, group]);

    const value = useMemo<DashboardStyleState>(() => {
        if (group === null) return NOT_ELIGIBLE;
        const ready = saved !== undefined;
        return { group, ready, style: saved ?? defaultStyleFor(group), setStyle };
    }, [group, saved, setStyle]);

    return <DashboardStyleContext.Provider value={value}>{children}</DashboardStyleContext.Provider>;
};

export const useDashboardStyle = (): DashboardStyleState => useContext(DashboardStyleContext);
